import React, { useEffect, useLayoutEffect, useRef, useState, useCallback } from 'react';
import { Loader2, CheckCircle2 } from 'lucide-react';

import { devLog, devWarn } from '../../utils/devLog';
import { parseGooglePlaceResult, validateAddress, AddressData } from '../../lib/utils/geocoding';
import { supabase } from '../../lib/supabase';
import { logAgent, AgentLogDetails } from '@rollops/app-shared/utils/agentLog';

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

interface AddressFormProps {
  initialData?: Partial<AddressData>;
  onValidate?: (isValid: boolean, addressData?: AddressData) => void;
  onCopyFromCompany?: () => void;
  disabled?: boolean;
  hideTitle?: boolean;
  placeholder?: string;
  requiredFields?: (keyof AddressData)[];
}

export function AddressForm({
  initialData = {},
  onValidate,
  onCopyFromCompany,
  disabled = false,
  hideTitle = false,
  placeholder = 'Enter address or business name...',
  requiredFields = ['street', 'city', 'state', 'zip'],
}: AddressFormProps) {
  const [addressData, setAddressData] = useState<Partial<AddressData>>({});
  const [mapUrl, setMapUrl] = useState<string | null>(null);
  const [inputValue, setInputValue] = useState('');
  const [apiError, setApiError] = useState<string | null>(null);
  const [predictions, setPredictions] = useState<Array<{ description: string; place_id: string; structured_formatting?: { main_text: string; secondary_text: string } }>>([]);
  const [showPredictions, setShowPredictions] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [allowManualEntry, setAllowManualEntry] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const predictionsRef = useRef<HTMLDivElement>(null);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isInitializedRef = useRef(false);
  const onValidateRef = useRef(onValidate);
  const previousInitialDataRef = useRef<Partial<AddressData>>({});
  const shouldMaintainFocusRef = useRef(false);
  const lastCursorPositionRef = useRef<{ start: number; end: number } | null>(null);
  const searchGenerationRef = useRef(0);

  // Keep onValidate ref updated without causing re-renders
  useEffect(() => {
    onValidateRef.current = onValidate;
  }, [onValidate]);

  const fallbackAgentLog = useCallback(
    async (payload: AgentLogDetails) => {
      const supabaseLoggingEnabled =
        process.env.REACT_APP_AGENT_LOG_SUPABASE_ENABLED === '1';
      if (!supabaseLoggingEnabled) {
        devLog(
          'Agent log suppressed because REACT_APP_AGENT_LOG_SUPABASE_ENABLED !== 1',
          payload,
        );
        return;
      }

      await supabase
        .from('agent_logs' as any)
        .insert({
          location: payload.location,
          message: payload.message,
          data: payload.data ? JSON.stringify(payload.data) : null,
          session_id: payload.sessionId ?? null,
          run_id: payload.runId ?? null,
          hypothesis_id: payload.hypothesisId ?? null,
          created_at: new Date(payload.timestamp ?? Date.now()).toISOString(),
        })
        .throwOnError();
    },
    [],
  );

  const logWithFallback = useCallback(
    (details: AgentLogDetails) => logAgent(details, fallbackAgentLog),
    [fallbackAgentLog],
  );

  // Restore focus synchronously after predictions update to prevent focus loss
  useLayoutEffect(() => {
    if (shouldMaintainFocusRef.current && inputRef.current) {
      const input = inputRef.current;
      // Always restore focus if we're maintaining it
      // Use setTimeout(0) to ensure DOM has updated
      const timeoutId = setTimeout(() => {
        if (input && inputRef.current === input) {
          input.focus();
          // Restore cursor position if we have it
          if (lastCursorPositionRef.current) {
            const { start, end } = lastCursorPositionRef.current;
            // Ensure positions are within bounds
            const maxPos = input.value.length;
            const safeStart = Math.min(Math.max(0, start), maxPos);
            const safeEnd = Math.min(Math.max(0, end), maxPos);
            try {
              input.setSelectionRange(safeStart, safeEnd);
            } catch (e) {
              // Ignore selection errors (can happen during rapid typing)
            }
          }
        }
        shouldMaintainFocusRef.current = false;
      }, 0);
      
      return () => clearTimeout(timeoutId);
    }
  }, [predictions, showPredictions, isLoading]);

  // Load map preview
  const loadMapPreview = useCallback((lat: number, lng: number) => {
    if (!GOOGLE_MAPS_API_KEY) {
      devWarn('[AddressForm] GOOGLE_MAPS_API_KEY not configured. Map preview will not be available.');
      return;
    }
    
    const mapUrlValue = `https://maps.googleapis.com/maps/api/staticmap?center=${lat},${lng}&zoom=15&size=400x200&markers=color:0xEBFFEE|${lat},${lng}&key=${GOOGLE_MAPS_API_KEY}`;
    devLog('[AddressForm] Loading map preview:', mapUrlValue.substring(0, 100) + '...');
    setMapUrl(mapUrlValue);
  }, []);

  // Search places using Edge Function (avoids CORS and API blocking)
  const searchPlaces = useCallback(async (input: string) => {
    logWithFallback({
      location: 'AddressForm.tsx:49',
      message: 'searchPlaces called - using edge function',
      data: {
        input,
        inputLength: input.length,
        hasApiKey: !!GOOGLE_MAPS_API_KEY,
        apiKeyPrefix: GOOGLE_MAPS_API_KEY ? `${GOOGLE_MAPS_API_KEY.substring(0, 10)}...` : 'MISSING',
        supabaseUrl: process.env.REACT_APP_SUPABASE_URL?.substring(0, 30) + '...' || 'MISSING',
      },
      sessionId: 'debug-session',
      runId: 'run1',
      hypothesisId: 'A',
    });

    if (!input || input.length < 3) {
      setPredictions([]);
      setShowPredictions(false);
      return;
    }

    // Check for API key - only show error if actually trying to use it
    // Don't block the component from rendering if key is missing (might be loading)
    if (!GOOGLE_MAPS_API_KEY) {
      // Only set error when user actually tries to search
      // This prevents false warnings on initial render
      return;
    }

    // Track search generation to discard stale results
    const currentGeneration = ++searchGenerationRef.current;

    setIsLoading(true);
    setApiError(null);

    try {
      logWithFallback({
        location: 'AddressForm.tsx:115',
        message: 'Calling edge function google-places-autocomplete',
        data: { input },
        sessionId: 'debug-session',
        runId: 'run1',
        hypothesisId: 'A',
      });

      let data: {
        status?: string;
        predictions?: Array<{ description: string; place_id: string; structured_formatting?: { main_text: string; secondary_text: string } }>;
        error_message?: string;
      } | null = null;
      let error: { message?: string; context?: unknown } | null = null;
      
      try {
        logWithFallback({
          location: 'AddressForm.tsx:106',
          message: 'About to invoke edge function',
          data: {
            input,
            apiKeyPrefix: GOOGLE_MAPS_API_KEY ? `${GOOGLE_MAPS_API_KEY.substring(0, 15)}...` : 'MISSING',
            timestamp: new Date().toISOString(),
          },
          sessionId: 'debug-session',
          runId: 'run2',
          hypothesisId: 'D',
        });

        const result = await supabase.functions.invoke('google-places-autocomplete', {
          body: {
            input,
          },
        });
        data = result.data;
        error = result.error;
      } catch (invokeError: unknown) {
        const err = invokeError as Error;
        logWithFallback({
          location: 'AddressForm.tsx:103',
          message: 'Edge function invoke exception',
          data: {
            error: err.message,
            stack: err.stack,
            name: err.name,
          },
          sessionId: 'debug-session',
          runId: 'run1',
          hypothesisId: 'C',
        });
        throw new Error(`Failed to invoke edge function: ${err.message}`);
      }

      logWithFallback({
        location: 'AddressForm.tsx:130',
        message: 'Edge function response received',
        data: {
          hasError: !!error,
          hasData: !!data,
          status: data?.status,
          errorMessage: error?.message,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          errorStatus: (error as any)?.status,
          errorContext: error?.context,
          dataErrorMsg: data?.error_message,
          predictionsCount: data?.predictions?.length || 0,
          fullResponse: JSON.stringify(data).substring(0, 500),
        },
        sessionId: 'debug-session',
        runId: 'run1',
        hypothesisId: 'A',
      });

      // Discard stale results if a newer search has started
      if (currentGeneration !== searchGenerationRef.current) {
        return;
      }

      if (error) {
        logWithFallback({
          location: 'AddressForm.tsx:142',
          message: 'Edge function error',
          data: { error: error.message, errorContext: error.context },
          sessionId: 'debug-session',
          runId: 'run1',
          hypothesisId: 'A',
        });
        throw new Error(error.message || 'Failed to fetch predictions');
      }

      if (!data) {
        throw new Error('No data returned from edge function');
      }

      // Check for API errors
      if (data.error_message) {
        // Special handling for referrer restriction error
        const isReferrerRestrictionError = data.error_message.includes('referer restrictions') || 
                                          data.error_message.includes('referrer restrictions');
        
        if (isReferrerRestrictionError) {
          setApiError(
            `⚠️ API Key Configuration Error: ${data.error_message}\n\n` +
            `✅ FIXED: Browser restrictions have been removed via gcloud CLI.\n` +
            `⏳ If you still see this error, wait 1-2 minutes for changes to propagate, then:\n` +
            `   1. Hard refresh the page (Cmd+Shift+R or Ctrl+Shift+R)\n` +
            `   2. Clear browser cache\n` +
            `   3. Restart the web app if needed\n\n` +
            `The API key is now configured correctly for edge functions.`
          );
        } else {
          setApiError(`Google API Error: ${data.error_message} (Status: ${data.status})`);
        }
        
        setPredictions([]);
        setShowPredictions(false);
        return;
      }

      if (data.status === 'OK' && data.predictions && data.predictions.length > 0) {
        logWithFallback({
          location: 'AddressForm.tsx:165',
          message: 'Predictions received successfully',
          data: { predictionsCount: data.predictions.length },
          sessionId: 'debug-session',
          runId: 'run1',
          hypothesisId: 'A',
        });
        // Mark that we should maintain focus when updating predictions
        shouldMaintainFocusRef.current = true;
        setPredictions(data.predictions);
        setShowPredictions(true);
        setApiError(null);
      } else if (data.status === 'ZERO_RESULTS') {
        setPredictions([]);
        setShowPredictions(false);
        setApiError(null);
      } else {
        setPredictions([]);
        setShowPredictions(false);
        setApiError(`API error: ${data.status}${data.error_message ? ` - ${data.error_message}` : ''}`);
      }
    } catch (err: unknown) {
      const error = err as Error;
      logWithFallback({
        location: 'AddressForm.tsx:185',
        message: 'searchPlaces exception',
        data: { error: error.message, stack: error.stack },
        sessionId: 'debug-session',
        runId: 'run1',
        hypothesisId: 'A',
      });
      setShowPredictions(false);
      setApiError(`Address search unavailable: ${error.message || 'Google Places API failed'}. You can enter your address manually below.`);
      setAllowManualEntry(true);
      // Allow proceeding with manual entry - don't throw error
    } finally {
      // Only clear loading if this is still the latest search
      if (currentGeneration === searchGenerationRef.current) {
        setIsLoading(false);
      }
    }
  }, [logWithFallback]);

  // Handle input change with debounce
  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value || '';

    // Update value immediately for responsive typing
    setInputValue(value);

    // Clear existing debounce timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }

    // Debounce search (500ms) - only if 3+ characters
    if (value.length >= 3) {
      debounceTimerRef.current = setTimeout(() => {
        // Mark that we should maintain focus when predictions arrive
        shouldMaintainFocusRef.current = true;
        lastCursorPositionRef.current = {
          start: inputRef.current?.selectionStart ?? value.length,
          end: inputRef.current?.selectionEnd ?? value.length,
        };
        searchPlaces(value);
      }, 500);
    } else {
      setPredictions([]);
      setShowPredictions(false);
    }
  }, [searchPlaces]);

  // Handle place selection using Edge Function
  const handlePlaceSelect = useCallback(async (placeId: string, description: string) => {
    if (!placeId) return;

    setIsLoading(true);
    setApiError(null);
    setShowPredictions(false);

    try {
      const { data, error } = await supabase.functions.invoke('google-places-details', {
        body: {
          place_id: placeId,
        },
      });

      if (error) {
        throw new Error(error.message || 'Failed to fetch place details');
      }

      if (data.error_message) {
        // Special handling for referrer restriction error
        const isReferrerRestrictionError = data.error_message.includes('referer restrictions') || 
                                          data.error_message.includes('referrer restrictions');
        
        if (isReferrerRestrictionError) {
          setApiError(
            `API Key Configuration Error: ${data.error_message}\n\n` +
            `Fix: Go to Google Cloud Console → APIs & Services → Credentials → Your API Key\n` +
            `Change "Application restrictions" from "HTTP referrers" to "None" (or "IP addresses" for production)\n` +
            `Edge functions make server-side requests, so HTTP referrer restrictions don't work.`
          );
        } else {
          setApiError(`Google API Error: ${data.error_message} (Status: ${data.status})`);
        }
        requestAnimationFrame(() => {
          onValidateRef.current?.(false);
        });
        return;
      }

      if (data.status === 'OK' && data.result) {
        const parsedAddress = parseGooglePlaceResult(data.result);
        
        const newAddressData: AddressData = {
          street: parsedAddress.street || '',
          unit: parsedAddress.unit || '',
          city: parsedAddress.city || '',
          state: parsedAddress.state || '',
          zip: parsedAddress.zip || '',
          country: parsedAddress.country || 'US',
          latitude: parsedAddress.latitude,
          longitude: parsedAddress.longitude,
          formatted_address: parsedAddress.formatted_address,
          place_id: parsedAddress.place_id,
          business_name: parsedAddress.business_name,
        };

        setAddressData(newAddressData);
        setInputValue(data.result.formatted_address || description);
        setApiError(null);

        // Load map preview
        if (parsedAddress.latitude && parsedAddress.longitude) {
          loadMapPreview(parsedAddress.latitude, parsedAddress.longitude);
        }

        // Validate and notify parent
        // Use requestAnimationFrame to defer validation until after render (prevents focus loss)
        const validation = validateAddress(newAddressData, requiredFields);
        requestAnimationFrame(() => {
          onValidateRef.current?.(validation.isValid, newAddressData);
        });
      } else {
        setApiError(data.error_message || `Failed to get place details: ${data.status}`);
        requestAnimationFrame(() => {
          onValidateRef.current?.(false);
        });
      }
    } catch (err: unknown) {
      const error = err as Error;
      setApiError(`Error: ${error.message || 'Failed to fetch place details'}`);
      setTimeout(() => {
        onValidateRef.current?.(false);
      }, 0);
    } finally {
      setIsLoading(false);
    }
  }, [requiredFields, loadMapPreview]);

  // Close predictions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        predictionsRef.current &&
        !predictionsRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setShowPredictions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Initialize from initialData and update when it changes
  // Only update if data actually changed to prevent infinite loops
  useEffect(() => {
    if (!initialData || Object.keys(initialData).length === 0) {
      return;
    }

    // Check if data actually changed by comparing key fields
    const previous = previousInitialDataRef.current;
    const hasChanged = 
      previous.street !== initialData.street ||
      previous.city !== initialData.city ||
      previous.state !== initialData.state ||
      previous.zip !== initialData.zip ||
      previous.latitude !== initialData.latitude ||
      previous.longitude !== initialData.longitude ||
      previous.place_id !== initialData.place_id ||
      previous.formatted_address !== initialData.formatted_address;

    // Skip if already initialized and nothing changed
    if (isInitializedRef.current && !hasChanged) {
      return;
    }

    const isInitialLoad = !isInitializedRef.current;

    // Build formatted address if we have address components
    if (initialData.formatted_address && initialData.formatted_address !== inputValue) {
      setInputValue(initialData.formatted_address);
    } else if (initialData.street && initialData.city && initialData.state) {
      const parts = [
        initialData.street,
        initialData.unit,
        initialData.city,
        initialData.state,
        initialData.zip,
      ].filter(Boolean);
      const formatted = parts.join(', ');
      if (formatted !== inputValue) {
        setInputValue(formatted);
      }
    }

    // Update addressData if it changed (we already checked hasChanged above)
    if (hasChanged) {
      setAddressData(initialData);
    }

    // Store current data for next comparison
    previousInitialDataRef.current = { ...initialData };
    isInitializedRef.current = true;
    
    // Only validate on initial load or if data actually changed from external source
    // Don't validate if this was triggered by our own onValidate callback (prevents loop)
    if (isInitialLoad && onValidateRef.current) {
      const validation = validateAddress(initialData, requiredFields);
      // Use requestAnimationFrame to defer validation until after render
      requestAnimationFrame(() => {
        onValidateRef.current?.(validation.isValid, initialData as AddressData);
      });
    }
  }, [initialData, requiredFields, inputValue]);

  // Load map when coordinates are available
  useEffect(() => {
    if (addressData.latitude && addressData.longitude && GOOGLE_MAPS_API_KEY) {
      loadMapPreview(addressData.latitude, addressData.longitude);
    }
  }, [addressData.latitude, addressData.longitude, loadMapPreview]);

  const hasAddress = addressData.street && addressData.city && addressData.state && addressData.zip;

  // Only show API key warning if user tries to interact and key is missing
  // Don't block rendering on initial load (key might be loading from env)
  const showApiKeyWarning = !GOOGLE_MAPS_API_KEY && (isLoading || apiError);
  
  if (showApiKeyWarning) {
    return (
      <div className="space-y-4">
        <div className="p-3 bg-red-900/20 border border-red-500/50 rounded-lg">
          <p className="text-sm text-red-400">Google Maps API key not configured. Please contact support.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {!hideTitle && (
        <h3 className="text-xl font-bold text-[#EBFFEE]">Address</h3>
      )}

      {/* Copy from Company link */}
      {onCopyFromCompany && (
        <button
          type="button"
          onClick={onCopyFromCompany}
          className="text-[#EBFFEE] hover:text-[#EBFFEE]/80 underline text-sm font-medium transition-colors inline-flex items-center gap-1"
        >
          <span>Copy from Company</span>
        </button>
      )}

      {/* Search Address Input */}
      <div className="space-y-2">
        <label className="block text-sm font-medium text-[#EBFFEE]">
          Search address or business name
          {requiredFields.includes('street') && <span className="text-rollops-accent"> *</span>}
        </label>
        <div className="relative">
          <input
            ref={inputRef}
            type="text"
            value={inputValue}
            onChange={handleInputChange}
            onFocus={() => {
              // Show predictions if available when input is focused
              if (predictions.length > 0) {
                setShowPredictions(true);
              }
            }}
            onBlur={() => {
              // Only blur if clicking outside (not on predictions dropdown)
              // Use setTimeout to check if focus moved to predictions
              setTimeout(() => {
                const activeElement = document.activeElement;
                // If focus moved to predictions dropdown, don't close it
                if (predictionsRef.current?.contains(activeElement)) {
                  // User clicked on a prediction, let it handle the selection
                  return;
                }
                // If focus is back on input, keep predictions open
                if (activeElement === inputRef.current) {
                  setShowPredictions(predictions.length > 0);
                  shouldMaintainFocusRef.current = true;
                  return;
                }
                // Otherwise, close predictions
                setShowPredictions(false);
                shouldMaintainFocusRef.current = false;
              }, 150);
            }}
            placeholder={placeholder}
            disabled={disabled}
            maxLength={200}
            className="w-full px-4 py-3 rounded-lg bg-[#504A4A] text-[#EBFFEE] placeholder:text-[#A9A3A3] border-none outline-none focus:ring-2 focus:ring-[#9B1421]/50 transition-all hover:bg-[#5A5454]"
            autoComplete="off"
            id="address-autocomplete-input"
          />
          {isLoading && (
            <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
              <Loader2 className="animate-spin text-[#EBFFEE]" size={16} />
            </div>
          )}

          {/* Predictions dropdown */}
          {showPredictions && predictions.length > 0 && (
            <div
              ref={predictionsRef}
              className="absolute z-[9999] w-full mt-1 bg-[#504A4A] border-2 border-[#9B1421] rounded-lg shadow-2xl max-h-60 overflow-y-auto"
              style={{ 
                top: 'calc(100% + 4px)', 
                left: 0,
              }}
            >
              {predictions.map((prediction) => (
                <div
                  key={prediction.place_id}
                  onMouseDown={(e) => {
                    // Prevent default to avoid stealing focus from input
                    e.preventDefault();
                    // Manually handle the selection
                    handlePlaceSelect(prediction.place_id, prediction.description);
                  }}
                  className="px-4 py-3 cursor-pointer hover:bg-[#5A5454] border-b border-white/10 last:border-b-0 transition-colors"
                >
                  <div className="text-[#EBFFEE] font-medium">
                    {prediction.structured_formatting?.main_text || prediction.description.split(',')[0]}
                  </div>
                  {prediction.structured_formatting?.secondary_text && (
                    <div className="text-[#A9A3A3] text-sm mt-1">
                      {prediction.structured_formatting.secondary_text}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Error message */}
        {apiError && (
          <div className="mt-2 p-3 bg-red-900/20 border border-red-500/50 rounded-lg">
            <p className="text-sm text-red-400">{apiError}</p>
          </div>
        )}
      </div>

      {/* Manual Address Entry - shown when API fails */}
      {allowManualEntry && (
        <div className="space-y-4">
          <div className="text-center">
            <p className="text-sm text-[#A9A3A3] mb-4">
              Or enter your address manually below
            </p>
          </div>

          {/* Street Address */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-[#EBFFEE]">
              Street Address <span className="text-rollops-accent">*</span>
            </label>
            <input
              type="text"
              value={addressData.street || ''}
              onChange={(e) => {
                const newData = { ...addressData, street: e.target.value };
                setAddressData(newData);
                requestAnimationFrame(() => {
                  onValidateRef.current?.(validateAddress(newData, requiredFields).isValid, newData);
                });
              }}
              placeholder="123 Main Street"
              disabled={disabled}
              maxLength={200}
              className="w-full px-4 py-3 rounded-lg bg-[#504A4A] text-[#EBFFEE] placeholder:text-[#A9A3A3] border-none outline-none focus:ring-2 focus:ring-[#9B1421]/50 disabled:opacity-50"
            />
          </div>

          {/* Unit/Suite (optional) */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-[#EBFFEE]">
              Unit/Suite (optional)
            </label>
            <input
              type="text"
              value={addressData.unit || ''}
              onChange={(e) => {
                const newData = { ...addressData, unit: e.target.value };
                setAddressData(newData);
                requestAnimationFrame(() => {
                  onValidateRef.current?.(validateAddress(newData, requiredFields).isValid, newData);
                });
              }}
              placeholder="Apt 4B, Suite 200"
              disabled={disabled}
              maxLength={50}
              className="w-full px-4 py-3 rounded-lg bg-[#504A4A] text-[#EBFFEE] placeholder:text-[#A9A3A3] border-none outline-none focus:ring-2 focus:ring-[#9B1421]/50 disabled:opacity-50"
            />
          </div>

          {/* City, State, ZIP row */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <label className="block text-sm font-medium text-[#EBFFEE]">
                City <span className="text-rollops-accent">*</span>
              </label>
              <input
                type="text"
                value={addressData.city || ''}
                onChange={(e) => {
                  const newData = { ...addressData, city: e.target.value };
                  setAddressData(newData);
                  requestAnimationFrame(() => {
                    onValidateRef.current?.(validateAddress(newData, requiredFields).isValid, newData);
                  });
                }}
                placeholder="City"
                disabled={disabled}
                maxLength={50}
                className="w-full px-4 py-3 rounded-lg bg-[#504A4A] text-[#EBFFEE] placeholder:text-[#A9A3A3] border-none outline-none focus:ring-2 focus:ring-[#9B1421]/50 disabled:opacity-50"
              />
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium text-[#EBFFEE]">
                State <span className="text-rollops-accent">*</span>
              </label>
              <input
                type="text"
                value={addressData.state || ''}
                onChange={(e) => {
                  const newData = { ...addressData, state: e.target.value };
                  setAddressData(newData);
                  requestAnimationFrame(() => {
                    onValidateRef.current?.(validateAddress(newData, requiredFields).isValid, newData);
                  });
                }}
                placeholder="State"
                disabled={disabled}
                maxLength={50}
                className="w-full px-4 py-3 rounded-lg bg-[#504A4A] text-[#EBFFEE] placeholder:text-[#A9A3A3] border-none outline-none focus:ring-2 focus:ring-[#9B1421]/50 disabled:opacity-50"
              />
            </div>
          </div>

          {/* ZIP Code */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-[#EBFFEE]">
              ZIP Code <span className="text-rollops-accent">*</span>
            </label>
            <input
              type="text"
              value={addressData.zip || ''}
              onChange={(e) => {
                const newData = { ...addressData, zip: e.target.value };
                setAddressData(newData);
                requestAnimationFrame(() => {
                  onValidateRef.current?.(validateAddress(newData, requiredFields).isValid, newData);
                });
              }}
              placeholder="12345"
              disabled={disabled}
              className="w-full px-4 py-3 rounded-lg bg-[#504A4A] text-[#EBFFEE] placeholder:text-[#A9A3A3] border-none outline-none focus:ring-2 focus:ring-[#9B1421]/50 disabled:opacity-50"
            />
          </div>

          {/* Manual entry warning */}
          <div className="p-3 bg-yellow-900/20 border border-yellow-500/50 rounded-lg">
            <p className="text-sm text-yellow-400">
              ⚠️ Manual address entry detected. This address will need to be verified by our team before billing can be enabled.
            </p>
          </div>
        </div>
      )}


      {/* Subtext */}
      <p className="text-sm text-[#EBFFEE]/60">* Address is for billing and tax reporting</p>

      {/* Selected Address Display */}
      {hasAddress && (
        <div className="bg-transparent border border-white rounded-lg p-4 space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-[#EBFFEE]">Selected Address</span>
            {addressData.latitude && addressData.longitude && (
              <div className="flex items-center gap-1">
                <CheckCircle2 size={16} className="text-rollops-accent" />
                <span className="text-xs text-[#EBFFEE]/60">Verified</span>
              </div>
            )}
          </div>
          <div className="space-y-1">
            {addressData.business_name && (
              <p className="text-sm font-semibold text-[#EBFFEE]">{addressData.business_name}</p>
            )}
            {addressData.street && (
              <p className="text-sm text-[#EBFFEE]">
                {addressData.street}
                {addressData.unit && `, ${addressData.unit}`}
              </p>
            )}
            <p className="text-sm text-[#EBFFEE]/80">
              {[addressData.city, addressData.state, addressData.zip]
                .filter(Boolean)
                .join(', ')}
            </p>
          </div>
          {mapUrl && (
            <div className="mt-3">
              <img
                src={mapUrl}
                alt="Address location"
                className="w-full h-32 object-cover rounded-lg border border-white/20"
                onError={(e) => {
                  console.error('[AddressForm] Map image failed to load. This may be due to API key restrictions.');
                  console.error('[AddressForm] For local development, add localhost:3000 to Google API key website restrictions.');
                  // Hide broken image
                  (e.target as HTMLImageElement).style.display = 'none';
                  // Show fallback message
                  const container = (e.target as HTMLImageElement).parentElement;
                  if (container) {
                    const fallback = document.createElement('div');
                    fallback.className = 'w-full h-32 flex items-center justify-center bg-[#504A4A] rounded-lg border border-white/20 text-[#A9A3A3] text-sm';
                    fallback.textContent = 'Map preview unavailable';
                    container.appendChild(fallback);
                  }
                }}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
